import {DatePipe, JsonPipe} from '@angular/common';
import {Component, computed, effect, inject, output, signal, untracked} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {HttpClient, HttpContext, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {TranslateModule} from '@ngx-translate/core';
import {CommentContextService} from '../../services/comment-context.service';
import {AuditActor, AuditPage, AuditRecord} from '../../models/HAL/audit-record.model';
import {SKIP_EXCEPTION_HANDLING} from '../../tokens/skip-exception-handling.token';
import {SnackBarService} from '../../services/snackbar.service';

type AuditPeriod = 'ALL' | 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'CUSTOM';

const EXPORT_UNAVAILABLE_MESSAGE = 'exceptions.client.auditExportUnavailable.modal.body';
const EXPORT_ERROR_MESSAGES: ReadonlySet<string> = new Set([
  'exceptions.client.auditExportTooLarge.modal.body',
  EXPORT_UNAVAILABLE_MESSAGE
]);

interface AuditDayGroup {
  key: string;
  date: Date;
  records: AuditRecord[];
  today: boolean;
  yesterday: boolean;
}

interface AuditActorOption extends AuditActor {
  id: string;
}

interface AuditExportFormat {
  value: string;
  label: string;
}

@Component({
  selector: 'app-audit-history',
  imports: [DatePipe, FormsModule, JsonPipe, MatButton, MatIconButton, MatIcon, MatMenuModule, TranslateModule],
  templateUrl: './audit-history.component.html',
  styleUrl: './audit-history.component.scss'
})
export class AuditHistoryComponent {
  closed = output<void>();

  readonly records = signal<AuditRecord[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly loadError = signal(false);
  readonly exporting = signal<string | null>(null);
  readonly selectedActorId = signal<string | null>(null);
  readonly selectedPeriod = signal<AuditPeriod>('ALL');
  readonly customFrom = signal('');
  readonly customTo = signal('');
  readonly filterOpen = signal(false);
  readonly expandedRecordIds = signal<ReadonlySet<string>>(new Set());
  readonly page = signal({size: 50, totalElements: 0, totalPages: 0, number: 0});
  readonly unfilteredTotal = signal(0);
  readonly actors = signal<ReadonlyMap<string, AuditActorOption>>(new Map());

  readonly exportFormats: readonly AuditExportFormat[] = [
    {value: 'pdf', label: 'PDF'},
    {value: 'csv', label: 'CSV'},
    {value: 'json', label: 'JSON'}
  ];

  private readonly httpClient = inject(HttpClient);
  private readonly panelContextService = inject(CommentContextService);
  private readonly snackBarService = inject(SnackBarService);
  private requestId = 0;
  private currentAuditLink: string | null = null;

  protected readonly getAuditLink = computed(() => this.panelContextService.panelHal()?._links.getLink('getAudit')?.href);
  protected readonly exportAuditLink = computed(() => this.panelContextService.panelHal()?._links.getLink('exportAudit')?.href);
  protected readonly hasActivePeriod = computed(() =>
    this.selectedPeriod() !== 'ALL'
    && (this.selectedPeriod() !== 'CUSTOM' || !!this.customFrom() || !!this.customTo())
  );
  protected readonly activeFilterCount = computed(() =>
    (this.selectedActorId() ? 1 : 0) + (this.hasActivePeriod() ? 1 : 0)
  );
  protected readonly actorOptions = computed(() =>
    [...this.actors().values()].sort((left, right) =>
      (left.displayName ?? left.type).localeCompare(right.displayName ?? right.type)
    )
  );
  protected readonly selectedActor = computed(() => {
    const actorId = this.selectedActorId();
    return actorId ? this.actors().get(actorId) : undefined;
  });
  protected readonly hasMore = computed(() => this.page().number + 1 < this.page().totalPages);
  protected readonly dayGroups = computed<AuditDayGroup[]>(() => {
    const groups = new Map<string, AuditRecord[]>();
    for (const record of this.records()) {
      const date = new Date(record.occurredAt);
      const key = this.toLocalDateKey(date);
      groups.set(key, [...(groups.get(key) ?? []), record]);
    }
    const today = this.startOfDay(new Date());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return [...groups.entries()].map(([key, records]) => {
      const date = this.parseLocalDate(key);
      return {
        key,
        date,
        records,
        today: date.getTime() === today.getTime(),
        yesterday: date.getTime() === yesterday.getTime()
      };
    });
  });

  constructor() {
    effect(() => {
      const auditLink = this.getAuditLink() ?? null;
      untracked(() => {
        if (auditLink && auditLink !== this.currentAuditLink) {
          this.resetForContext(auditLink);
        } else if (!auditLink) {
          this.clear();
        }
      });
    });
  }

  close(): void {
    this.closed.emit();
  }

  toggleFilters(): void {
    this.filterOpen.update(open => !open);
  }

  selectActor(actorId: string | null): void {
    this.selectedActorId.set(actorId);
    this.reload();
  }

  selectPeriod(period: AuditPeriod): void {
    this.selectedPeriod.set(period);
    this.customFrom.set('');
    this.customTo.set('');
    this.reload();
  }

  setCustomDate(boundary: 'from' | 'to', value: string): void {
    if (boundary === 'from') {
      this.customFrom.set(value);
    } else {
      this.customTo.set(value);
    }
    this.reload();
  }

  resetFilters(): void {
    this.selectedActorId.set(null);
    this.selectedPeriod.set('ALL');
    this.customFrom.set('');
    this.customTo.set('');
    this.reload();
  }

  removePeriodFilter(): void {
    this.selectedPeriod.set('ALL');
    this.customFrom.set('');
    this.customTo.set('');
    this.reload();
  }

  retryLoad(): void {
    this.reload();
  }

  loadMore(): void {
    if (this.loadingMore() || !this.hasMore()) {
      return;
    }
    this.loadPage(this.page().number + 1, true);
  }

  toggleRecord(recordId: string): void {
    const expanded = new Set(this.expandedRecordIds());
    if (expanded.has(recordId)) {
      expanded.delete(recordId);
    } else {
      expanded.add(recordId);
    }
    this.expandedRecordIds.set(expanded);
  }

  isExpanded(recordId: string): boolean {
    return this.expandedRecordIds().has(recordId);
  }

  actorInitials(actor?: AuditActor): string {
    const name = actor?.displayName?.trim();
    if (!name) {
      return '';
    }
    return name.split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  }

  eventParams(record: AuditRecord): Record<string, unknown> {
    return {
      subject: this.eventSubject(record),
      role: record.resolved?.participantRole ?? '',
      oldState: record.details?.['oldState'] ?? '',
      newState: record.details?.['newState'] ?? '',
      format: record.details?.['format'] ?? ''
    };
  }

  exportAudit(format: string): void {
    const link = this.exportAuditLink();
    if (!link || this.exporting() || this.customDatesInvalid()) {
      return;
    }
    const url = this.buildFilteredUrl(link);
    url.searchParams.set('format', format);
    this.exporting.set(format);
    const context = new HttpContext().set(SKIP_EXCEPTION_HANDLING, true);
    this.httpClient.get(url.href, {context, observe: 'response', responseType: 'blob'}).subscribe({
      next: response => {
        this.downloadResponse(response, format);
        this.exporting.set(null);
        this.snackBarService.open('audit.export.success', 3);
      },
      error: (error: HttpErrorResponse) => {
        this.exporting.set(null);
        void this.exportErrorMessage(error).then(message => this.snackBarService.open(message, 8, 'error'));
      }
    });
  }

  private async exportErrorMessage(error: HttpErrorResponse): Promise<string> {
    const clientBody = await this.readClientBody(error.error);
    if (clientBody && EXPORT_ERROR_MESSAGES.has(clientBody)) {
      return clientBody;
    }
    return error.status === 503 ? EXPORT_UNAVAILABLE_MESSAGE : 'audit.export.error';
  }

  private async readClientBody(body: unknown): Promise<string | undefined> {
    if (!(body instanceof Blob)) {
      return undefined;
    }
    try {
      return JSON.parse(await body.text())?.clientBody;
    } catch {
      return undefined;
    }
  }

  protected customDatesInvalid(): boolean {
    return !!this.customFrom() && !!this.customTo() && this.customFrom() > this.customTo();
  }

  private resetForContext(link: string): void {
    this.currentAuditLink = link;
    this.actors.set(new Map());
    this.unfilteredTotal.set(0);
    this.selectedActorId.set(null);
    this.selectedPeriod.set('ALL');
    this.customFrom.set('');
    this.customTo.set('');
    this.filterOpen.set(false);
    this.expandedRecordIds.set(new Set());
    this.records.set([]);
    this.loadPage(0, false);
  }

  private clear(): void {
    this.currentAuditLink = null;
    this.requestId++;
    this.records.set([]);
    this.actors.set(new Map());
    this.page.set({size: 50, totalElements: 0, totalPages: 0, number: 0});
    this.unfilteredTotal.set(0);
    this.loading.set(false);
    this.loadingMore.set(false);
    this.loadError.set(false);
  }

  private reload(): void {
    if (!this.currentAuditLink) {
      return;
    }
    this.expandedRecordIds.set(new Set());
    if (this.customDatesInvalid()) {
      this.requestId++;
      this.records.set([]);
      this.page.update(page => ({...page, totalElements: 0, totalPages: 0, number: 0}));
      this.loading.set(false);
      this.loadingMore.set(false);
      this.loadError.set(false);
      return;
    }
    this.loadPage(0, false);
  }

  private loadPage(pageNumber: number, append: boolean): void {
    const link = this.currentAuditLink;
    if (!link) {
      return;
    }
    const requestId = ++this.requestId;
    if (append) {
      this.loadingMore.set(true);
    } else {
      this.loading.set(true);
      this.loadError.set(false);
    }
    const url = this.buildFilteredUrl(link);
    url.searchParams.set('page', pageNumber.toString());
    url.searchParams.set('size', '50');
    const context = new HttpContext().set(SKIP_EXCEPTION_HANDLING, true);
    this.httpClient.get<AuditPage>(url.href, {context}).subscribe({
      next: response => {
        if (requestId !== this.requestId) {
          return;
        }
        const newRecords = response._embedded?.auditRecords ?? [];
        this.rememberActors(newRecords);
        this.records.set(append ? this.appendNew(newRecords) : newRecords);
        this.page.set(response.page);
        if (this.activeFilterCount() === 0) {
          this.unfilteredTotal.set(response.page.totalElements);
        }
        this.loading.set(false);
        this.loadingMore.set(false);
      },
      error: () => {
        if (requestId !== this.requestId) {
          return;
        }
        if (!append) {
          this.records.set([]);
          this.loadError.set(true);
        } else {
          this.snackBarService.open('audit.loadError.more', 3, 'error');
        }
        this.loading.set(false);
        this.loadingMore.set(false);
      }
    });
  }

  private appendNew(records: AuditRecord[]): AuditRecord[] {
    const loadedIds = new Set(this.records().map(loaded => loaded.id));
    return [...this.records(), ...records.filter(next => !loadedIds.has(next.id))];
  }

  private rememberActors(records: AuditRecord[]): void {
    const actors = new Map(this.actors());
    for (const record of records) {
      if (record.actor?.id) {
        actors.set(record.actor.id, {...record.actor, id: record.actor.id});
      }
    }
    this.actors.set(actors);
  }

  private buildFilteredUrl(link: string): URL {
    const url = new URL(link.replace(/\{[?&][^}]*}$/, ''));
    const actorId = this.selectedActorId();
    if (actorId) {
      url.searchParams.set('actorId', actorId);
    }
    const {from, to} = this.dateRange();
    if (from) {
      url.searchParams.set('from', from.toISOString());
    }
    if (to) {
      url.searchParams.set('to', to.toISOString());
    }
    return url;
  }

  private dateRange(): {from?: Date; to?: Date} {
    const now = new Date();
    switch (this.selectedPeriod()) {
      case 'TODAY':
        return {from: this.startOfDay(now)};
      case 'LAST_7_DAYS':
        return {from: this.daysAgo(now, 6)};
      case 'LAST_30_DAYS':
        return {from: this.daysAgo(now, 29)};
      case 'CUSTOM':
        return {
          from: this.customFrom() ? this.parseLocalDate(this.customFrom()) : undefined,
          to: this.customTo() ? this.nextDay(this.parseLocalDate(this.customTo())) : undefined
        };
      default:
        return {};
    }
  }

  private daysAgo(date: Date, days: number): Date {
    const result = this.startOfDay(date);
    result.setDate(result.getDate() - days);
    return result;
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private nextDay(date: Date): Date {
    const result = this.startOfDay(date);
    result.setDate(result.getDate() + 1);
    return result;
  }

  private toLocalDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private eventSubject(record: AuditRecord): string {
    const resolved = record.resolved;
    return resolved?.commentContent
      ?? resolved?.actionDocumentTitle
      ?? resolved?.documentTitle
      ?? resolved?.fileName
      ?? resolved?.caseName
      ?? resolved?.actionType
      ?? resolved?.participantRole
      ?? record.objectId;
  }

  private downloadResponse(response: HttpResponse<Blob>, format: string): void {
    const blob = response.body ?? new Blob();
    const contentDisposition = response.headers.get('Content-Disposition') ?? '';
    const filenameMatch = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
    const filename = filenameMatch ? this.decodeFilename(filenameMatch[1]) : `case-audit.${format}`;
    const url = globalThis.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    setTimeout(() => {
      anchor.remove();
      globalThis.URL.revokeObjectURL(url);
    }, 1000);
  }

  private decodeFilename(filename: string): string {
    try {
      return decodeURIComponent(filename);
    } catch {
      return filename;
    }
  }
}
